// VsAircallPage.js — SEO landing page targeting "Aircall alternative Australia"
// and "Aircall AI Voice vs" queries. Public, no auth.
// Positions VoiceFabric as the coaching depth Aircall AI Voice can't reach,
// and the "keep your Yabbit UCaaS" story vs Aircall's UCaaS-replacement pitch.
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, XCircle, ArrowRight, Sparkles, DollarSign, Layers, Zap, ShieldCheck, Phone } from 'lucide-react';

const COMPARISON_ROWS = [
  { label: 'Per-user cost (AUD)', aircall: 'A$60 phone plan + A$76 AI Voice add-on', vf: 'A$49 – A$149 / user / mo (all-in)', highlight: true },
  { label: 'Does it replace your phone system?', aircall: 'Yes — Aircall IS the UCaaS', vf: 'No — VoiceFabric sits on Yabbit UCaaS', highlight: true },
  { label: 'Roles covered', aircall: 'Sales + support focus', vf: 'Sales · Support · Admin · Accounts · Management' },
  { label: 'Depth of coaching', aircall: 'Summaries, sentiment, talk-ratio', vf: 'KB-graded scoring · evidence calls · reassessment · governance' },
  { label: 'Reception / admin / accounts grading', aircall: 'Not built for it', vf: 'Native — separate role KBs per team' },
  { label: 'Included minutes per user', aircall: 'Bundled with phone plan', vf: '60 – 300 mins / user (grading + sentiment)' },
  { label: 'Overage pricing published', aircall: 'Quoted per plan', vf: '$0.07/min transcription · $0.09/min sentiment' },
  { label: 'Australian data residency', aircall: 'EU / US hosted', vf: 'AU-hosted · tenant-isolated' },
  { label: 'Governance-independent escalation', aircall: 'Not offered', vf: 'Yes — compliance flags independent of score' },
  { label: 'White-label / reseller', aircall: 'Not offered', vf: '50% reseller margin available' },
  { label: 'Time to deploy', aircall: 'Full UCaaS migration (weeks)', vf: '4 weeks · no phone-system change' },
];

const Chip = ({ ok, children }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${ok ? 'bg-teal-500/15 text-teal-300' : 'bg-red-500/15 text-red-300'}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
    {children}
  </span>
);

const VsAircallPage = () => (
  <div className="min-h-screen bg-[#0A0F1C]" data-testid="vs-aircall-page">
    {/* Header */}
    <header className="border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-teal-500 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="text-white font-bold">Yabbit VoiceFabric</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/why-voicefabric" className="text-white/60 hover:text-white">Why VoiceFabric</Link>
          <Link to="/pricing" className="text-white/60 hover:text-white">Pricing</Link>
          <Link to="/login" className="text-teal-300 hover:text-teal-200 font-semibold" data-testid="vs-aircall-signin">Sign in →</Link>
        </div>
      </div>
    </header>

    {/* Hero */}
    <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
      <p className="text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">Aircall AI Voice · Alternative for Australian teams</p>
      <h1 className="text-4xl sm:text-5xl font-black text-white leading-tight tracking-tight">
        Aircall gives you summaries.<br /><span className="text-teal-400">We give you coaching that changes behaviour.</span>
      </h1>
      <p className="text-white/70 text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
        Aircall AI Voice is a great sales-and-support summariser — but it&apos;s an add-on that only works inside the Aircall UCaaS.
        Yabbit VoiceFabric grades every call against <strong className="text-white">your knowledge base</strong>, coaches the specific criterion the rep missed,
        and tracks the score lift — all on top of your existing Yabbit phone system.
      </p>
      <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-aircall-pricing-cta"
        >
          See our pricing
          <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/free-audit"
          className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          data-testid="vs-aircall-audit-cta"
        >
          Free 3-call audit
        </Link>
      </div>
    </section>

    {/* At-a-glance callouts */}
    <section className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-3 gap-4">
      {[
        { icon: Phone, title: 'Keep your Yabbit UCaaS', body: 'Aircall AI Voice only works inside Aircall. VoiceFabric works inside Yabbit — the phones you already run.' },
        { icon: Layers, title: 'Coaching, not just summaries', body: 'KB-graded criteria · evidence calls · reassessment tracking · governance streaks. Depth Aircall doesn\'t attempt.' },
        { icon: DollarSign, title: 'One bill, not phone + AI add-on', body: 'A$49-149/user/mo covers everything. Aircall is phone plan + AI Voice add-on stacked.' },
      ].map((c) => (
        <div key={c.title} className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <c.icon className="w-6 h-6 text-teal-400 mb-3" />
          <h3 className="text-white font-bold text-lg">{c.title}</h3>
          <p className="text-white/60 text-sm mt-1">{c.body}</p>
        </div>
      ))}
    </section>

    {/* Comparison table */}
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="vs-aircall-comparison">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Side-by-side · Aircall AI Voice vs VoiceFabric</h2>
        <p className="text-white/50 text-sm mt-3">Pricing as of Feb 2026, sourced from Aircall&apos;s public pricing page + AU reseller quotes.</p>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/60 text-xs uppercase tracking-wider bg-white/5">
              <th className="px-4 py-3 text-left">Feature</th>
              <th className="px-4 py-3 text-left">Aircall AI Voice (add-on)</th>
              <th className="px-4 py-3 text-left text-teal-300">Yabbit VoiceFabric</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {COMPARISON_ROWS.map((r) => (
              <tr key={r.label} className={r.highlight ? 'bg-teal-500/5' : ''}>
                <td className="px-4 py-3 text-white/80 font-medium">{r.label}</td>
                <td className="px-4 py-3 text-white/50"><Chip ok={false}>{r.aircall}</Chip></td>
                <td className="px-4 py-3"><Chip ok>{r.vf}</Chip></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-white/30 text-[11px] mt-4 italic text-center">
        Aircall figures from aircall.io public pricing (Professional/Custom + AI Voice add-on ≈ US$49/user/mo).
        AUD conversions at 1 USD ≈ 1.55 AUD. Feb 2026 snapshot — check aircall.io for latest.
      </p>
    </section>

    {/* The three real reasons */}
    <section className="max-w-4xl mx-auto px-6 py-16 space-y-10">
      <div className="text-center mb-6">
        <h2 className="text-3xl sm:text-4xl font-black text-white">Three reasons Yabbit teams pick VoiceFabric over Aircall AI</h2>
      </div>

      {[
        {
          n: '01',
          title: 'Summaries are not coaching',
          body: 'Aircall AI Voice produces a call summary, a sentiment score, and a talk-ratio. That\'s a great note-taker. VoiceFabric grades each call against your own knowledge base, tells the rep the specific criterion they missed, links three calls where they proved they can do it right, and tracks whether next month\'s calls actually improved. Different product category.',
        },
        {
          n: '02',
          title: 'Aircall is scoped to sales and support — VoiceFabric grades the whole business',
          body: 'Reception, accounts, admin, management coaching conversations — Aircall AI doesn\'t model any of these. VoiceFabric ships with role KBs for all five, editable per tenant, with distinct scoring floors and governance rules for each.',
        },
        {
          n: '03',
          title: 'You don\'t change carriers to add coaching',
          body: 'Aircall requires you to move your entire phone system to Aircall. VoiceFabric plugs into your Yabbit UCaaS in 4 weeks — no number porting, no line audits, no reception retraining. You keep the phone system that works and add the coaching that finally moves the numbers.',
        },
      ].map((r) => (
        <div key={r.n} className="flex gap-6 items-start">
          <div className="text-6xl font-black text-teal-500/40 leading-none">{r.n}</div>
          <div>
            <h3 className="text-white font-bold text-xl mb-2">{r.title}</h3>
            <p className="text-white/70 leading-relaxed">{r.body}</p>
          </div>
        </div>
      ))}
    </section>

    {/* Honest — when Aircall is the right pick */}
    <section className="max-w-3xl mx-auto px-6 py-12">
      <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-6">
        <h3 className="text-amber-300 font-bold text-lg mb-3">Honest answer: when Aircall is the right choice</h3>
        <p className="text-white/70 leading-relaxed">
          If you&apos;re a <strong className="text-white">10-30 seat SaaS sales team</strong> that wants a modern, simple cloud phone + built-in AI summarisation, Aircall is a lovely product.
          The onboarding is fast, the UI is clean, and the CRM integrations are good.
        </p>
        <p className="text-white/70 leading-relaxed mt-3">
          If you&apos;re an <strong className="text-white">Australian phone-heavy business</strong> that already has a UCaaS (Yabbit or otherwise) and wants deep,
          KB-graded coaching across every role that touches the phone — VoiceFabric is a different tier of product, at the same or lower price.
        </p>
      </div>
    </section>

    {/* Bottom CTA */}
    <section className="max-w-4xl mx-auto px-6 py-16 text-center">
      <div className="bg-gradient-to-br from-teal-500/10 via-teal-500/5 to-transparent border border-teal-500/30 rounded-3xl p-10">
        <h2 className="text-3xl sm:text-4xl font-black text-white leading-tight">
          Aircall notes what happened.<br />VoiceFabric changes what happens next.
        </h2>
        <p className="text-white/70 text-lg mt-4 max-w-xl mx-auto">
          Upload three of your recordings. We&apos;ll grade them against your knowledge base and show you what coaching looks like when it&apos;s tied to a criterion — not just a smiley face.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <Link to="/free-audit"
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-400 text-white font-bold px-8 py-4 rounded-lg text-lg transition-colors"
            data-testid="vs-aircall-final-cta"
          >
            Get my free audit
            <ArrowRight className="w-5 h-5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 text-teal-300 hover:text-teal-200 font-semibold px-4 py-4"
          >
            See pricing →
          </Link>
        </div>
      </div>
    </section>

    {/* Trust bar */}
    <section className="max-w-5xl mx-auto px-6 py-16 border-t border-white/5">
      <div className="grid md:grid-cols-3 gap-8 text-center">
        <div>
          <ShieldCheck className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Australian data residency</h3>
          <p className="text-white/50 text-sm mt-1">AU-hosted · tenant-isolated · Privacy Act 2026 ready</p>
        </div>
        <div>
          <Zap className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">Live in 4 weeks</h3>
          <p className="text-white/50 text-sm mt-1">Onboarding call · KB workshop · baseline scoring by week 3</p>
        </div>
        <div>
          <Layers className="w-8 h-8 text-teal-400 mx-auto mb-3" />
          <h3 className="text-white font-semibold">5-role grading</h3>
          <p className="text-white/50 text-sm mt-1">Sales, support, admin, accounts, management — one platform</p>
        </div>
      </div>
    </section>

    <footer className="border-t border-white/5 py-8 text-center text-white/30 text-xs">
      © 2026 Yabbit VoiceFabric · yabbit.com.au · <Link to="/pricing" className="hover:text-white/50">Pricing</Link> · <Link to="/why-voicefabric" className="hover:text-white/50">Why VoiceFabric</Link> · <Link to="/vs-dialpad" className="hover:text-white/50">vs Dialpad</Link>
    </footer>
  </div>
);

export default VsAircallPage;
